import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { API_BASE } from "@/lib/api";

export default function AdminOverview() {
    const [stats, setStats] = useState({ users: 0, pending: 0, divisions: 0, teams: 0, articles: 0 });

    useEffect(() => {
        (async () => {
            try {
                const [u, p, d, t, a] = await Promise.all([
                    api.get("/admin/users"),
                    api.get("/admin/users?status=pending"),
                    api.get("/divisions"),
                    api.get("/teams"),
                    api.get("/regulation"),
                ]);
                setStats({
                    users: u.data.length,
                    pending: p.data.length,
                    divisions: d.data.length,
                    teams: t.data.length,
                    articles: a.data.length,
                });
            } catch {}
        })();
    }, []);

    return (
        <div data-testid="admin-overview">
            <h2 className="op-heading text-3xl mb-6">Panoramica</h2>
            <div className="grid grid-cols-2 lg:grid-cols-5 border border-t22252a divide-x divide-y lg:divide-y-0 divide-t22252a">
                <Stat label="Utenti totali" value={stats.users} />
                <Stat label="In attesa" value={stats.pending} highlight={stats.pending > 0} />
                <Stat label="Divisioni" value={stats.divisions} />
                <Stat label="Squadre" value={stats.teams} max={56} />
                <Stat label="Articoli" value={stats.articles} />
            </div>

            <div className="mt-10 op-card p-6">
                <div className="op-label text-t2bb6ff">// PROSSIMI STEP</div>
                <h3 className="op-heading text-2xl mt-3">Cosa puoi fare adesso</h3>
                <ul className="mt-4 space-y-2 text-tc9ccd3 list-disc list-inside text-sm leading-relaxed">
                    <li>Creare le 56 squadre dalla sezione <strong>Squadre</strong> assegnandole alla divisione giusta.</li>
                    <li>Approvare i presidenti che si registrano dalla sezione <strong>Utenti</strong>.</li>
                    <li>Promuovere/retrocedere squadre a fine stagione dal pulsante <strong>Sposta</strong>.</li>
                    <li>Modificare o aggiungere articoli nel <strong>Regolamento</strong>.</li>
                </ul>
            </div>

            <BackupCard />
        </div>
    );
}

function BackupCard() {
    const [counts, setCounts] = useState(null);
    const [downloading, setDownloading] = useState(false);
    const [err, setErr] = useState("");

    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get("/admin/backup/counts");
                setCounts(data);
            } catch {
                // user might not be admin or endpoint not yet deployed
            }
        })();
    }, []);

    const download = async () => {
        setDownloading(true);
        setErr("");
        try {
            // Force browser download. Use fetch + blob so cookies (httpOnly auth) travel correctly.
            const resp = await fetch(`${API_BASE}/admin/backup/snapshot`, {
                credentials: "include",
            });
            if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
            const blob = await resp.blob();
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            const cd = resp.headers.get("content-disposition") || "";
            const m = /filename="([^"]+)"/.exec(cd);
            a.href = url;
            a.download = m ? m[1] : `op-snapshot-${Date.now()}.json.gz`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);
        } catch (e) {
            setErr(e.message || "Errore download");
        } finally {
            setDownloading(false);
        }
    };

    if (!counts) return null;

    return (
        <div className="mt-6 op-card p-6" data-testid="backup-card">
            <div className="op-label text-t26ff6b">// BACKUP MANUALE</div>
            <h3 className="op-heading text-2xl mt-3">Snapshot stato torneo</h3>
            <p className="text-tc9ccd3 text-sm mt-2 leading-relaxed">
                Scarica un file <strong>.json.gz</strong> con tutte le {Object.keys(counts.collections).length} collection
                chiave ({counts.total_docs.toLocaleString()} documenti totali). Usalo prima di operazioni delicate
                (chiusura mercato, avanzamento stagione) come punto di ripristino.
            </p>
            <div className="mt-4 flex items-center gap-3 flex-wrap">
                <button
                    onClick={download}
                    disabled={downloading}
                    className="op-btn op-btn-primary text-sm disabled:opacity-60"
                    data-testid="backup-download-btn"
                >
                    {downloading ? "Generazione…" : "↓ Scarica backup (.json.gz)"}
                </button>
                {err && <span className="op-mono text-xs text-tff3d3d">{err}</span>}
            </div>
            <details className="mt-4">
                <summary className="op-mono text-[10px] text-t8f95a3 cursor-pointer hover:text-tffffff uppercase">
                    Dettaglio collection
                </summary>
                <div className="mt-2 grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-1 op-mono text-[11px]">
                    {Object.entries(counts.collections).map(([c, n]) => (
                        <div key={c} className="flex justify-between border-b border-t22252a py-1">
                            <span className="text-t8f95a3">{c}</span>
                            <span className="text-tffffff">{n.toLocaleString()}</span>
                        </div>
                    ))}
                </div>
            </details>
        </div>
    );
}

function Stat({ label, value, max, highlight }) {
    return (
        <div className={`p-5 ${highlight ? "bg-te8ff00/5" : ""}`}>
            <div className="op-label">{label}</div>
            <div className={`mt-2 op-mono text-3xl font-bold ${highlight ? "text-te8ff00" : "text-tffffff"}`}>
                {value}
                {max ? <span className="text-t5c6370 text-base"> / {max}</span> : null}
            </div>
        </div>
    );
}
